import {
  BriefcaseIcon,
  CameraIcon,
  ChatAlt2Icon,
  DotsVerticalIcon,
  PhoneIcon,
  RefreshIcon,
} from '@heroicons/react/outline';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import PhoneFrame from './PhoneFrame';
import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The owner's own phone: the WhatsApp Business app, still in use, listing the
 * chats the agent has answered from the same number.
 */
export default function WhatsAppBusinessApp({
  chats,
}: {
  chats: { name: string; preview: string; time: string; avatar: string }[];
}) {
  const { t } = useChannelsT();

  const nav = [
    { label: t('Chats'), icon: ChatAlt2Icon, active: true },
    { label: t('Updates'), icon: RefreshIcon, active: false },
    { label: t('Tools'), icon: BriefcaseIcon, active: false },
    { label: t('Calls'), icon: PhoneIcon, active: false },
  ];

  return (
    <div className='relative mx-auto max-w-[27rem] pb-2 sm:pb-0'>
      <PhoneFrame
        bar='bg-white text-[#111b21] dark:bg-[#111b21] dark:text-[#e9edef]'
        className='ring-1 ring-white/15 sm:me-0 sm:ms-auto'
      >
        <div className='bg-white text-[#111b21] dark:bg-[#111b21] dark:text-[#e9edef]'>
          <div className='flex items-center gap-3 px-4 pb-2 pt-1'>
            <p className='min-w-0 flex-1 truncate text-[19px] font-semibold tracking-[-0.01em] text-[#008069] dark:text-[#e9edef]'>
              WhatsApp Business
            </p>
            <CameraIcon className='h-5 w-5 flex-none text-[#54656f] dark:text-[#aebac1]' />
            <DotsVerticalIcon className='h-5 w-5 flex-none text-[#54656f] dark:text-[#aebac1]' />
          </div>
          <div className='flex gap-2 px-4 pb-2 pt-1 text-[12px] font-medium'>
            <span className='rounded-full bg-[#d8fdd2] px-3 py-1 text-[#15603e] dark:bg-[#103529] dark:text-[#d8fdd2]'>
              {t('All')}
            </span>
            <span className='rounded-full bg-[#f0f2f5] px-3 py-1 text-[#54656f] dark:bg-[#202c33] dark:text-[#8696a0]'>
              {t('Unread')}
            </span>
          </div>
          <ul className='min-h-[22rem]'>
            {chats.map(({ name, preview, time, avatar }) => (
              <li key={name} className='flex items-center gap-3 px-4 py-2.5'>
                <span
                  className={clsxm(
                    'flex h-11 w-11 flex-none items-center justify-center rounded-full text-[15px] font-semibold text-white',
                    avatar,
                  )}
                >
                  {name.charAt(0)}
                </span>
                <span className='min-w-0 flex-1 border-b border-[#e9edef] pb-2.5 dark:border-[#222d34]'>
                  <span className='flex items-baseline justify-between gap-2'>
                    <span className='truncate text-[14.5px] font-semibold'>
                      {name}
                    </span>
                    <span className='flex-none text-[11px] tabular-nums text-[#667781] dark:text-[#8696a0]'>
                      {time}
                    </span>
                  </span>
                  <span className='mt-0.5 flex items-center gap-1 text-[13px] text-[#667781] dark:text-[#8696a0]'>
                    <svg
                      viewBox='0 0 16 11'
                      aria-hidden='true'
                      className='h-[11px] w-4 flex-none text-[#53bdeb]'
                    >
                      <path
                        d='M11.1.6 4.6 7.8 1.7 5 .6 6.1l4 3.9L12.2 1.7 11.1.6zm3.8 0L8.4 7.8l-.9-.9-1.1 1.1 2 2L16 1.7 14.9.6z'
                        fill='currentColor'
                      />
                    </svg>
                    <span className='truncate'>{preview}</span>
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <div className='grid grid-cols-4 border-t border-[#e9edef] px-1 pb-4 pt-2 dark:border-[#222d34]'>
            {nav.map(({ label, icon: Icon, active }) => (
              <span
                key={label}
                className='flex min-w-0 flex-col items-center gap-1 text-[10.5px] font-medium'
              >
                <span
                  className={clsxm(
                    'flex h-7 w-12 items-center justify-center rounded-full',
                    active
                      ? 'bg-[#d8fdd2] text-[#15603e] dark:bg-[#103529] dark:text-[#d8fdd2]'
                      : 'text-[#54656f] dark:text-[#aebac1]',
                  )}
                >
                  <Icon className='h-[18px] w-[18px]' />
                </span>
                <span
                  className={clsxm(
                    'whitespace-nowrap text-[10px] tracking-[-0.01em]',
                    active
                      ? 'text-[#111b21] dark:text-[#e9edef]'
                      : 'text-[#54656f] dark:text-[#8696a0]',
                  )}
                >
                  {label}
                </span>
              </span>
            ))}
          </div>
        </div>
      </PhoneFrame>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:bottom-24 sm:start-0 sm:mt-0 sm:w-[15.5rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(500)}
      >
        <p className={clsxm('text-[13px] font-semibold', tone.ink)}>
          {t('Messages work in both places')}
        </p>
        <ul className='mt-3 space-y-2 text-[13px]'>
          {[t('WhatsApp Business app'), t('Your AI agent')].map((item) => (
            <li
              key={item}
              className={clsxm('flex items-center gap-2', tone.body)}
            >
              <span className='flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
                <CheckGlyph className='h-3 w-3' />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
